import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, User, LogOut, CreditCard, Settings as SettingsIcon, Receipt, Menu, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function Topbar({ onToggleSidebar }) {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [notifs, setNotifs] = useState([]);
  const [openN, setOpenN] = useState(false);
  const [openU, setOpenU] = useState(false);
  const nRef = useRef(null);
  const uRef = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (nRef.current && !nRef.current.contains(e.target)) setOpenN(false);
      if (uRef.current && !uRef.current.contains(e.target)) setOpenU(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    api.get("/notifications").then(r => setNotifs(r.data.notifications || [])).catch(()=>{});
    const t = setInterval(() => {
      api.get("/notifications").then(r => setNotifs(r.data.notifications || [])).catch(()=>{});
    }, 60000);
    return () => clearInterval(t);
  }, []);

  const [walletCredits, setWalletCredits] = useState(null);
  useEffect(() => {
    const load = () => api.get("/me/wallet").then(r => setWalletCredits(r.data.remaining)).catch(()=>{});
    load();
    window.addEventListener("bf:credits-updated", load);
    return () => window.removeEventListener("bf:credits-updated", load);
  }, []);
  const credits = user?.role === "admin" ? "∞" : (walletCredits ?? user?.credits ?? 0).toLocaleString();

  const plan = (user?.plan || "free").toLowerCase();
  const planBadge = plan === "free"
    ? "bg-white/[0.04] border-white/[0.08] text-slate-300"
    : "bg-gradient-to-r from-[#6366F1]/15 to-[#8B5CF6]/10 border-[#6366F1]/35 text-[#A5B4FC]";

  return (
    <header className="sticky top-0 z-20 backdrop-blur-xl bg-[#080B12]/80 border-b border-white/[0.06] px-4 md:px-6 py-3 flex items-center gap-3">
      <button data-testid="topbar-hamburger" aria-label="Apri menu" onClick={onToggleSidebar} className="lg:hidden h-10 w-10 flex items-center justify-center rounded-lg hover:bg-white/[0.05] text-slate-300">
        <Menu className="w-5 h-5"/>
      </button>

      <div className="flex-1"/>

      {/* Plan + credits chip (desktop) */}
      <button
        onClick={() => nav("/app/billing")}
        data-testid="topbar-plan-chip"
        className={`hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs transition-all hover:brightness-110 ${planBadge}`}
      >
        <Sparkles className="w-3.5 h-3.5"/>
        <span className="font-semibold uppercase tracking-wider">{plan}</span>
        <span className="w-px h-3 bg-white/15"/>
        <span className="text-slate-400">Crediti</span>
        <span data-testid="topbar-credits" className="font-mono font-semibold text-emerald-300">{credits}</span>
      </button>

      {/* Compact credits chip (mobile only) */}
      <button
        onClick={() => nav("/app/billing")}
        data-testid="topbar-credits-chip-mobile"
        aria-label="Crediti disponibili"
        className={`md:hidden inline-flex items-center gap-1.5 h-9 px-2.5 rounded-full border text-xs transition-all active:scale-95 ${planBadge}`}
      >
        <Sparkles className="w-3.5 h-3.5"/>
        <span data-testid="topbar-credits-mobile" className="font-mono font-semibold text-emerald-300">{credits}</span>
      </button>

      {/* Notifications */}
      <div className="relative" ref={nRef}>
        <button
          data-testid="topbar-notifications-btn"
          aria-label="Notifiche"
          onClick={()=>setOpenN(!openN)}
          className="relative h-10 w-10 flex items-center justify-center rounded-lg hover:bg-white/[0.05] text-slate-300 hover:text-white transition-colors"
        >
          <Bell className="w-5 h-5"/>
          {notifs.length > 0 && <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] ring-2 ring-[#080B12]"/>}
        </button>
        {openN && (
          <div className="absolute right-0 top-full mt-2 w-80 bg-[#111827] border border-white/[0.08] rounded-xl shadow-2xl p-2 bf-animate-in">
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold px-2 py-2">Notifiche</p>
            {notifs.length === 0 ? (
              <p className="text-xs text-slate-500 italic p-3">Nessuna notifica.</p>
            ) : notifs.map(n => (
              <button key={n.id} data-testid={`notif-${n.id}`} onClick={()=>{ setOpenN(false); nav(n.cta_href); }} className="w-full text-left p-2.5 rounded-lg hover:bg-white/[0.04] transition-colors">
                <p className="text-sm text-slate-200">{n.title}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">{n.body}</p>
                <p className="text-[10px] text-[#A5B4FC] mt-1 font-medium">{n.cta} →</p>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* User dropdown */}
      <div className="relative" ref={uRef}>
        <button data-testid="topbar-user-btn" onClick={()=>setOpenU(!openU)} className="flex items-center gap-2 p-1 pr-2.5 rounded-lg hover:bg-white/[0.05] transition-colors">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center text-xs font-bold text-white shadow-[0_4px_14px_-4px_rgba(99,102,241,0.6)]">
            {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
          </div>
          <span className="hidden md:inline text-sm text-slate-200">{(user?.first_name || user?.name || "").split(" ")[0]}</span>
        </button>
        {openU && (
          <div className="absolute right-0 top-full mt-2 w-60 bg-[#111827] border border-white/[0.08] rounded-xl shadow-2xl py-1 bf-animate-in">
            <div className="px-3 py-2.5 border-b border-white/[0.06]">
              <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
            <UserMenuItem icon={User} label="Profilo" onClick={()=>{setOpenU(false); nav("/app/settings");}} testid="um-profile"/>
            <UserMenuItem icon={SettingsIcon} label="Impostazioni" onClick={()=>{setOpenU(false); nav("/app/settings");}} testid="um-settings"/>
            <UserMenuItem icon={CreditCard} label="Abbonamento" onClick={()=>{setOpenU(false); nav("/app/billing");}} testid="um-sub"/>
            <UserMenuItem icon={Receipt} label="Billing" onClick={()=>{setOpenU(false); nav("/app/billing");}} testid="um-billing"/>
            <div className="border-t border-white/[0.06] my-1"/>
            <UserMenuItem icon={LogOut} label="Logout" danger onClick={logout} testid="um-logout"/>
          </div>
        )}
      </div>
    </header>
  );
}

function UserMenuItem({ icon: Icon, label, onClick, danger, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2.5 transition-colors ${danger ? "text-red-300 hover:bg-red-500/10" : "text-slate-300 hover:bg-white/[0.04] hover:text-white"}`}
    >
      <Icon className="w-4 h-4"/><span>{label}</span>
    </button>
  );
}
